/**
 * @module ui/Leaderboard
 * Local leaderboard — reads/writes top scores from localStorage.
 *
 * Security (T-02, T-06):
 *  - parseLeaderboard() is the single authoritative read-time validator.
 *    Never call JSON.parse() directly on leaderboard storage.
 *  - sanitizeName() strips HTML tags before any name is stored or rendered.
 *  - renderLeaderboard() uses textContent exclusively — never innerHTML.
 *
 * Legacy class (LocalLeaderboard) kept for any pre-existing references.
 */

import type { LeaderboardEntry, GameMode } from '../types.js';

export type { LeaderboardEntry };

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STORAGE_KEY = 'fruitSlasher:leaderboard';
const MAX_ENTRIES = 10;
const MAX_NAME_LENGTH = 16;
const MAX_SCORE = 999999;

// ---------------------------------------------------------------------------
// Functional API (per module contract)
// ---------------------------------------------------------------------------

/**
 * Strip all HTML tags from the input string and trim to max 16 characters.
 * Returns 'Player' if the result is empty after stripping.
 *
 * Security note (T-02): call this on all player-supplied name strings before
 * storage and before rendering. Even though PIXI.Text and textContent are
 * inherently XSS-safe, sanitizing at the data boundary is defense-in-depth.
 */
export function sanitizeName(raw: string): string {
  // Strip complete HTML tags FIRST, then any stray angle brackets (a payload
  // truncated mid-tag would otherwise survive the tag regex), THEN cap length.
  const stripped = raw
    .replace(/<[^>]*>/g, '')
    .replace(/[<>]/g, '')
    .trim()
    .slice(0, MAX_NAME_LENGTH);
  return stripped.length > 0 ? stripped : 'Player';
}

/**
 * Parse and validate raw localStorage JSON into a LeaderboardEntry array.
 * Rejects entries that fail schema checks (wrong types, score out of [0, MAX_SCORE],
 * invalid mode). Re-sanitizes name on each entry even if previously sanitized.
 * Returns [] on JSON parse error or if input is null.
 *
 * Security note (T-02, T-06): this is the defense-in-depth read-time sanitization
 * that prevents a tampered localStorage entry from causing XSS if rendering code
 * ever regresses to innerHTML. Call this function; never call JSON.parse() directly
 * on leaderboard storage.
 */
export function parseLeaderboard(raw: string | null): LeaderboardEntry[] {
  if (raw === null) return [];

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }

  if (!Array.isArray(parsed)) return [];

  const result: LeaderboardEntry[] = [];
  for (const item of parsed) {
    if (item === null || typeof item !== 'object') continue;

    const rec = item as Record<string, unknown>;
    const name = rec['name'];
    const score = rec['score'];
    const mode = rec['mode'];
    const date = rec['date'];

    if (
      typeof name !== 'string' ||
      typeof score !== 'number' ||
      !Number.isFinite(score) ||
      score < 0 ||
      score > MAX_SCORE ||
      (mode !== 'classic' && mode !== 'zen') ||
      typeof date !== 'string'
    ) {
      continue;
    }

    result.push({
      name: sanitizeName(name),
      score: Math.floor(score),
      mode: mode,
      date,
    });
  }

  return result;
}

/**
 * Load top-N entries from localStorage for the given mode.
 * Returns [] if localStorage is unavailable (private browsing).
 * Internally calls parseLeaderboard() — all entries are schema-validated and
 * re-sanitized on read before being returned. Malformed entries are silently dropped.
 *
 * @param mode  - Filter by 'classic' or 'zen'.
 * @param limit - Maximum entries to return (default 10).
 */
export function getLeaderboard(mode: GameMode, limit = MAX_ENTRIES): LeaderboardEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const all = parseLeaderboard(raw);
    return all
      .filter((e) => e.mode === mode)
      .sort((a, b) => b.score - a.score)
      .slice(0, limit);
  } catch {
    return [];
  }
}

/**
 * Persist a new entry. Maintains top-10 sorted by score descending.
 * name is sanitised via sanitizeName() before write (strip HTML tags, max 16 chars).
 * Silent no-op if localStorage is unavailable.
 */
export function saveEntry(entry: LeaderboardEntry): void {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const existing = parseLeaderboard(raw);

    const sanitized: LeaderboardEntry = {
      ...entry,
      name: sanitizeName(entry.name),
      score: Math.floor(entry.score),
    };

    existing.push(sanitized);
    existing.sort((a, b) => b.score - a.score);
    const trimmed = existing.slice(0, MAX_ENTRIES);

    localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
  } catch {
    // localStorage unavailable (private browsing, quota) — fail silently.
  }
}

/**
 * Render the leaderboard table into the given container element.
 * SECURITY: uses textContent (never innerHTML) for all dynamic text.
 * Names are rendered via textContent; scores and dates are primitives.
 *
 * @param containerEl    - Target DOM element to render into.
 * @param mode           - Which mode's scores to display.
 * @param highlightScore - Optionally highlight entries matching this score.
 */
export function renderLeaderboard(
  containerEl: HTMLElement,
  mode: GameMode,
  highlightScore?: number,
): void {
  // Clear existing content safely (no innerHTML).
  while (containerEl.firstChild) {
    containerEl.removeChild(containerEl.firstChild);
  }

  const entries = getLeaderboard(mode);

  if (entries.length === 0) {
    const empty = document.createElement('p');
    empty.textContent = 'No scores yet. Be the first!';
    containerEl.appendChild(empty);
    return;
  }

  const table = document.createElement('table');
  table.setAttribute('aria-label', `${mode} leaderboard`);

  // Header row.
  const thead = document.createElement('thead');
  const headerRow = document.createElement('tr');
  for (const col of ['#', 'Name', 'Score', 'Date']) {
    const th = document.createElement('th');
    th.textContent = col; // safe: hardcoded string
    headerRow.appendChild(th);
  }
  thead.appendChild(headerRow);
  table.appendChild(thead);

  // Data rows.
  const tbody = document.createElement('tbody');
  entries.forEach((entry, idx) => {
    const tr = document.createElement('tr');
    if (highlightScore !== undefined && entry.score === highlightScore) {
      tr.setAttribute('data-highlight', 'true');
    }

    const rankCell = document.createElement('td');
    rankCell.textContent = String(idx + 1); // number — XSS-safe

    const nameCell = document.createElement('td');
    nameCell.textContent = entry.name; // sanitized at save-time + re-sanitized on read

    const scoreCell = document.createElement('td');
    scoreCell.textContent = String(entry.score); // number — XSS-safe

    const dateCell = document.createElement('td');
    // Render only the date portion (YYYY-MM-DD) from the ISO string.
    dateCell.textContent = entry.date.slice(0, 10); // ISO date — hardcoded format

    tr.appendChild(rankCell);
    tr.appendChild(nameCell);
    tr.appendChild(scoreCell);
    tr.appendChild(dateCell);
    tbody.appendChild(tr);
  });

  table.appendChild(tbody);
  containerEl.appendChild(table);
}

// ---------------------------------------------------------------------------
// Legacy class API (kept for pre-existing wiring)
// ---------------------------------------------------------------------------

/** @deprecated Use functional API (getLeaderboard, saveEntry) instead. */
export class LocalLeaderboard {
  private _entries: LeaderboardEntry[];

  constructor() {
    this._entries = this._load();
  }

  /** Submits a new score. Returns the updated list. */
  submit(entry: Omit<LeaderboardEntry, 'date'>): LeaderboardEntry[] {
    const full: LeaderboardEntry = { ...entry, date: new Date().toISOString() };
    saveEntry(full);
    this._entries = this._load();
    return this._entries;
  }

  /** Returns the current top-N entries (all modes). */
  getEntries(): LeaderboardEntry[] {
    return this._entries;
  }

  /** Personal best for the given mode, or null if none. */
  personalBest(mode: GameMode): number | null {
    const bests = this._entries
      .filter((e) => e.mode === mode)
      .map((e) => e.score);
    return bests.length > 0 ? (bests[0] ?? null) : null;
  }

  private _load(): LeaderboardEntry[] {
    try {
      return parseLeaderboard(localStorage.getItem(STORAGE_KEY));
    } catch {
      return [];
    }
  }
}
